import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { CheckCircle2, Circle, Loader2, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  BatchUploadProgress,
  getBatchUploadDisplayCurrent,
} from "./batchUploadTypes";

type BatchUploadProgressPanelProps = {
  progress: BatchUploadProgress;
  className?: string;
};

const statusIcon = (status: BatchUploadProgress["items"][0]["status"]) => {
  switch (status) {
    case "uploading":
      return <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary" />;
    case "done":
      return <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" />;
    case "error":
      return <XCircle className="h-4 w-4 shrink-0 text-red-600" />;
    default:
      return <Circle className="h-4 w-4 shrink-0 text-gray-300" />;
  }
};

export const BatchUploadProgressPanel = ({
  progress,
  className,
}: BatchUploadProgressPanelProps) => {
  const { t } = useTranslation();
  const displayCurrent = getBatchUploadDisplayCurrent(progress);
  const percent =
    progress.total > 0
      ? Math.min(100, Math.round((displayCurrent / progress.total) * 100))
      : 0;
  const activeItem = progress.items.find((item) => item.status === "uploading");

  return (
    <div
      className={cn(
        "rounded-lg border border-slate-200 bg-slate-50 p-4 flex flex-col gap-3",
        className,
      )}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="flex flex-col gap-1">
        <p className="text-sm font-semibold text-slate-800">
          {t("mediaLibrary.batch.sequentialTitle")}
        </p>
        <p className="text-xs text-slate-600">
          {t("mediaLibrary.batch.sequentialHint")}
        </p>
      </div>

      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between text-xs text-slate-600">
          <span>
            {displayCurrent} / {progress.total} {t("mediaLibrary.batch.uploadProgress")}
          </span>
          <span>{percent}%</span>
        </div>
        <Progress value={percent} className="h-2" />
      </div>

      {activeItem && (
        <p className="text-xs">
          {t("mediaLibrary.batch.processingFile")} : <span className="font-normal text-primary font-medium truncate">{activeItem.fileName}</span>
        </p>
      )}

      <ul className="flex flex-col gap-1.5 max-h-[200px] overflow-y-auto">
        {progress.items.map((item, index) => (
          <li
            key={`${item.fileName}-${index}`}
            className={cn(
              "flex items-center gap-2 rounded-md px-2 py-1.5 text-sm",
              item.status === "uploading" && "bg-white border border-primary/20",
              item.status === "done" && "text-slate-600",
              item.status === "pending" && "text-slate-400",
              item.status === "error" && "bg-red-50 text-red-700",
            )}
          >
            {statusIcon(item.status)}
            <span className="truncate flex-1">{item.fileName}</span>
            <span className="text-xs shrink-0 text-slate-500">
              {item.status === "uploading" &&
                t("mediaLibrary.batch.statusUploading")}
              {item.status === "done" && t("mediaLibrary.batch.statusDone")}
              {item.status === "pending" && t("mediaLibrary.batch.statusPending")}
              {item.status === "error" && t("mediaLibrary.batch.statusError")}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};
